import * as yup from "yup";

/**
 * Cost type enum values (must match backend ClaimCostType enum)
 */
export const COST_TYPES = {
  LEAK_DETECTION: "leak_detection",
  REPAIR: "repair",
  DRYING: "drying",
  RESTORATION: "restoration",
  ELECTRICITY: "electricity",
  RENT_LOSS: "rent_loss",
  RENT_REDUCTION: "rent_reduction",
  ACCOMMODATION: "accommodation",
  VEHICLE_REPAIR: "vehicle_repair",
  EXPERT_REPORT: "expert_report",
  TOWING: "towing",
  RENTAL_CAR: "rental_car",
  RETURN_TRIP: "return_trip",
  OTHER: "other",
};

/**
 * The helpers below take the cost type configuration fetched from
 * `GET claim-cost-types` (a list of `{ value, label, amount_label,
 * show_fields, data_fields, upload_fields }`).
 */
const findType = (config, costType) =>
  config?.find((type) => type.value === costType);

/**
 * The select options for the cost types an editor offers, in catalog order.
 * Without a list every type is offered; the type of a row being edited is
 * always included so it stays selectable.
 */
export const costTypeOptions = (config, costTypes, currentType) =>
  (config ?? [])
    .filter(
      ({ value }) =>
        !costTypes || costTypes.includes(value) || value === currentType
    )
    .map(({ value, label }) => ({ key: value, label }));

export const getCostTypeLabel = (config, costType) =>
  findType(config, costType)?.label;

export const getAmountLabel = (config, costType) =>
  findType(config, costType)?.amount_label;

export const getAttachmentLabel = (config, costType, fieldName) =>
  findType(config, costType)?.upload_fields.find(
    (field) => field.name === fieldName
  )?.label || "Anhang";

/**
 * Get all attachment field names for a given cost type
 */
export const getAttachmentFields = (config, costType) =>
  findType(config, costType)?.upload_fields.map((field) => field.name) || [];

/**
 * Check if a field name is an attachment field for a given cost type
 */
export const isAttachmentField = (config, costType, fieldName) =>
  getAttachmentFields(config, costType).includes(fieldName);

/**
 * Base schema for common fields
 */
const baseSchema = {
  cost_type: yup.string().required("Kostenart ist erforderlich"),
  amount: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Betrag darf nicht negativ sein"),
  company_name: yup.string().nullable(),
  invoice_number: yup.string().nullable(),
  invoice_date: yup.date().nullable(),
};

/**
 * Get the validation schema for a specific cost type
 */
export const getSchemaForType = (config, costType) => {
  const type = findType(config, costType);
  if (!type) {
    return yup.object().shape(baseSchema);
  }

  const dataShape = {};
  type.data_fields.forEach((field) => {
    if (field.type === "number") {
      dataShape[field.name] = yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        );
    } else if (field.type === "date") {
      dataShape[field.name] = yup.date().nullable();
    } else {
      dataShape[field.name] = yup.string().nullable();
    }
  });

  return yup.object().shape({
    ...baseSchema,
    data: yup.object().shape(dataShape),
  });
};

/**
 * Get the full validation schema (used for the form)
 */
export const costEntrySchema = yup.object().shape({
  cost_type: yup.string().required("Kostenart ist erforderlich"),
  amount: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Betrag darf nicht negativ sein"),
  company_name: yup.string().nullable(),
  invoice_number: yup.string().nullable(),
  invoice_date: yup.date().nullable(),
  data: yup.object().nullable(),
  is_assigned: yup.boolean().nullable(),
});
